import { useState } from "react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import Portrait from "@/components/Portrait";
import LeaderDialog from "@/components/LeaderDialog";
import HEROES from "@/lib/pageHeroes.json";

// Abu Dhabi — Etihad Towers from the Corniche

function LeaderGroup({ label, people, onOpen }) {
  const { t, L } = useLang();
  if (!people.length) return null;
  return (
    <div data-testid={`leader-group-${label}`}>
      <p className="eyebrow mb-7">{t(label)}</p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {people.map((p, i) => (
          <Reveal key={p.id} delay={0.06 * i}>
            <button
              type="button"
              onClick={() => onOpen(p)}
              data-testid={`leader-card-${p.id}`}
              className="group h-full w-full overflow-hidden border border-border bg-card/40 text-start card-lift"
              style={{ borderRadius: "var(--radius)" }}
            >
              <Portrait name={p.name} photoUrl={p.photo_url} className="h-64 w-full" monogramClass="text-4xl" />
              <div className="p-6">
                <h3 className="font-head text-2xl leading-tight">{p.name}</h3>
                <p className="mt-1 text-sm text-primary">{L(p.title)}</p>
                <p className="mt-4 line-clamp-3 text-sm text-muted-foreground leading-relaxed">{L(p.bio)}</p>
                <span className="mt-5 inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {t("leader_read_bio")}
                </span>
              </div>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export default function Leadership() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const leaders = site?.leaders || [];
  const [head, ...rest] = leaders;
  const [active, setActive] = useState(null);
  const pillars = s.governance_pillars || [];

  return (
    <div data-testid="leadership-page">
      <Seo page="leadership" />
      <PageHero eyebrow={t("leaders_eyebrow")} title={t("leaders_title")} image={HEROES["leadership"]} />

      {head && (
        <section data-testid="chairman-spotlight" className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="grid gap-10 md:grid-cols-12 md:items-stretch">
              <div className="md:col-span-5 lg:col-span-4">
                <Portrait name={head.name} photoUrl={head.photo_url} className="h-[360px] w-full md:h-full" monogramClass="text-7xl" />
              </div>
              <div className="md:col-span-7 lg:col-span-8 flex flex-col justify-center border border-border bg-card/40 p-8 md:p-12" style={{ borderRadius: "var(--radius)" }}>
                <p className="eyebrow mb-3">{L(head.title)}</p>
                <h2 className="font-head text-4xl md:text-5xl leading-[1.05]" data-testid="chairman-spotlight-name">{head.name}</h2>
                <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">{L(head.bio)}</p>
                {L(s.chairman_quote) && (
                  <blockquote className="mt-9 border-s-2 border-primary ps-6 font-head text-xl md:text-2xl leading-snug text-foreground/90">
                    “{L(s.chairman_quote)}”
                  </blockquote>
                )}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {rest.length > 0 && (
        <section data-testid="leaders-grid" className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 md:pb-28 space-y-16">
          <LeaderGroup label="leaders_board" people={rest.filter((p) => p.group !== "advisory")} onOpen={setActive} />
          <LeaderGroup label="leaders_advisory" people={rest.filter((p) => p.group === "advisory")} onOpen={setActive} />
        </section>
      )}

      {pillars.length > 0 && (
        <section data-testid="governance-principles" className="border-t border-border bg-card/25">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
            <Reveal>
              <p className="eyebrow mb-4">{t("principles_eyebrow")}</p>
              <h2 className="font-head text-3xl md:text-5xl max-w-2xl leading-[1.08]">{t("principles_title")}</h2>
            </Reveal>
            <div className="mt-14 grid gap-px border border-border md:grid-cols-3" style={{ borderRadius: "var(--radius)", overflow: "hidden", background: "hsl(var(--border))" }}>
              {pillars.map((p, i) => (
                <Reveal key={i} delay={0.08 * i} className="bg-background">
                  <div data-testid={`pillar-${i}`} className="h-full p-8 md:p-10">
                    <span className="font-head text-5xl text-primary/25">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-5 font-head text-2xl leading-tight">{L(p.title)}</h3>
                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{L(p.body)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <LeaderDialog leader={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />
    </div>
  );
}
